import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect
} from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type { EhsFormioReactProps, FormioBuilderSchema } from '../index';
import { EhsFormioReactComponent } from '../index';
import type { Component } from '@formio/react';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import type {
  CheckboxOption,
  ExtendedCheckboxOption,
  EhsFormioCheckboxGroupSettings,
  EhsFormioCheckboxGroupConstructor
} from './EhsFormioCheckboxGroupUtil';
import {
  createDefaultValueForOther,
  findSelectOption,
  isArrayOfCheckboxOptions,
  isArrayOfStrings,
  transformDefaultValuesToCheckboxOptions,
  updateCheckboxGroupValue,
  filterOther,
  OTHER_LABEL,
  OTHER_LABEL_TRANSLATED
} from './EhsFormioCheckboxGroupUtil';
import {
  Checkbox,
  CheckboxGroup,
  CheckboxGroupItem,
  TextInput
} from '../../../form';
import { HBox } from '../../../layout';
import { isEmpty } from 'lodash';

export type OtherOption = {
  selectLabel: string;
  translatedLabel: string;
  selectValue: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  isSelected: boolean;
  setSelected: React.Dispatch<React.SetStateAction<boolean>>;
};

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export const EhsFormioOpenChoiceCheckboxGroupType =
  'ehsOpenChoiceCheckboxGroup';

export class EhsFormioOpenChoiceCheckboxGroup extends EhsFormioReactComponent<
  EhsFormioCheckboxGroupSettings,
  EhsFormioCheckboxGroupConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioOpenChoiceCheckboxGroupType,
      label: 'common.unknown',
      key: EhsFormioOpenChoiceCheckboxGroupType,
      classNames: '',
      ...extend
    });
  }

  //Begin Non static definitions-------------------------------------
  private groupData: Array<CheckboxOption> = [];
  private selectOptions = [{ selectLabel: '', selectValue: '' }];
  private classNames = '';
  extractFields(component: Component<EhsFormioCheckboxGroupConstructor>): void {
    this.shouldSetValue = true;
    this.classNames = component.classNames;
    this.dataForSetting = this._data[component.key as string];
    this.selectOptions = component.selectOptions;
    this.groupData = isArrayOfCheckboxOptions(this.dataForSetting)
      ? this.dataForSetting
      : [];
  }

  // provide our custom logic to Formio
  attachEhsReact(): [
    (
      props: EhsFormioReactProps<EhsFormioCheckboxGroupSettings>
    ) => React.JSX.Element,
    EhsFormioCheckboxGroupSettings
  ] {
    return [
      OpenChoiceCheckboxGroupCmp,
      {
        classNames: this.classNames,
        selectOptions: this.selectOptions,
        groupData: this.groupData
      }
    ];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * Checkboxes with an additional other option that when selected
 * allows the user to input custom text.
 *
 * Note: the component can only be updated via selectValue, therefore
 * we handle the custom text from the user as the selectValue and selectLabel is
 * set to 'common.other'.
 *
 * @param props formio {@link EhsFormioCheckboxGroupSettings} properties
 */
function OpenChoiceCheckboxGroupCmp(
  props: EhsFormioReactProps<EhsFormioCheckboxGroupSettings>
): React.JSX.Element {
  const { onChange, defaultValue, settings } = props;

  const handleChange = useCallback(
    (newValue: CheckboxOption[]): void => {
      onChange(((newValue as unknown) as string) ?? '');
    },
    [onChange]
  );

  const [otherValue, setOtherValue] = useState(() =>
    createDefaultValueForOther(
      settings.selectOptions,
      settings.groupData.length !== 0
        ? settings.groupData.map(cb => cb.selectValue)
        : defaultValue
    )
  );

  // whether the other option is selected
  const [isOtherSelected, setIsOtherSelected] = useState(
    () => !isEmpty(otherValue)
  );

  const otherOption: OtherOption = useMemo(
    () => ({
      selectLabel: OTHER_LABEL,
      translatedLabel: OTHER_LABEL_TRANSLATED,
      selectValue: otherValue,
      setValue: setOtherValue,
      isSelected: isOtherSelected,
      setSelected: setIsOtherSelected
    }),
    [isOtherSelected, otherValue]
  );

  // model for the CheckboxGroup
  // translate labels and add selected state
  const translatedItems: ExtendedCheckboxOption[] = useMemo(() => {
    return settings.selectOptions.map(option => {
      if (isArrayOfCheckboxOptions(defaultValue)) {
        // do not translate?
        if (!option.selectValue) {
          return { ...option, selected: false } as ExtendedCheckboxOption;
        }
      }

      // is the option already selected?
      const isInPropsValue =
        isArrayOfCheckboxOptions(props.value) &&
        props.value.includes(option.selectValue);

      // see if value is selected by default?
      const isInGroupData =
        settings.groupData.some(
          groupData => groupData.selectValue === option.selectValue
        ) ?? false;

      const isDefaultValueSelected =
        defaultValue?.includes(option.selectValue) ?? false;

      // translate labels
      return {
        selectValue: option.selectValue,
        selectLabel: intl(
          option.selectLabel as LanguageProperty,
          option.selectLabel
        ),
        selected: isDefaultValueSelected || isInPropsValue || isInGroupData
      } as ExtendedCheckboxOption;
    });
  }, [defaultValue, props.value, settings.groupData, settings.selectOptions]);

  return useMemo(() => {
    // filter out other option, label may or not be translated depending on how options
    // are passed to formio (strings vs CheckboxOptions)
    const filteredTranslatedItems = translatedItems.filter(
      option =>
        option.selectLabel !== OTHER_LABEL_TRANSLATED &&
        option.selectLabel !== OTHER_LABEL
    );

    return (
      <FormioCheckboxGroup
        {...props}
        defaultValue={defaultValue}
        translatedItems={filteredTranslatedItems}
        handleChange={handleChange}
        otherItem={otherOption}
      />
    );
  }, [props, defaultValue, translatedItems, handleChange, otherOption]);
}

type FormioCheckboxGroupProps = Pick<
  EhsFormioReactProps<EhsFormioCheckboxGroupSettings>,
  'settings' | 'label' | 'readOnly' | 'keyForComponent' | 'required'
> & {
  translatedItems: ExtendedCheckboxOption[];
  value?: string | string[] | CheckboxOption[] | null;
  handleChange: (newValue: CheckboxOption[]) => void;
  defaultValue: string[];
  otherItem: OtherOption;
};

function FormioCheckboxGroup({
  label,
  defaultValue,
  keyForComponent,
  readOnly,
  required,
  translatedItems,
  handleChange,
  value,
  settings,
  otherItem
}: Readonly<FormioCheckboxGroupProps>): React.JSX.Element {
  const { errorMessage } = useQuestionnaireValidation(keyForComponent);
  const otherTextInputRef = useRef<HTMLInputElement>(null);
  useEffect(
    () => {
      if (otherItem.isSelected && !isEmpty(value)) {
        otherTextInputRef.current?.focus();
      }
    },
    // we only want to focus the text input when the other item is selected, not in draft mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [otherItem]
  );

  const onChangeWrapper = useCallback(
    ({
      newValue,
      selectValue
    }: {
      newValue: boolean;
      selectValue: ExtendedCheckboxOption['selectValue'];
    }): void => {
      let option = findSelectOption(selectValue, translatedItems);

      // We handle the other option specially, since it's value is not static.
      // The text input passes the other label so we can identify that it should be updated.
      // If the other option been changed, then we also try to remove it from the existing selection.
      const isFilterOther = selectValue === otherItem.selectLabel;
      // build other option
      if (!option && isFilterOther) {
        option = {
          selectLabel: otherItem.selectLabel,
          // get the new value from the textInput
          selectValue: otherTextInputRef?.current?.value ?? ''
        };
      }

      if (!option) {
        console.error('Option not found');
        return;
      }

      // if formio value is empty, just add our new option
      if (!value || value === '' || value.length === 0) {
        handleChange([option]);
        return;
      }

      // if formio is handling values as strings
      if (isArrayOfStrings(value)) {
        // convert to CheckBoxOption[]
        const defaultCheckboxGroupValue = transformDefaultValuesToCheckboxOptions(
          defaultValue,
          translatedItems,
          true
        );

        // new Checkbox selected
        if (newValue) {
          handleChange([...defaultCheckboxGroupValue, option]);
          return;
        }

        // Checkbox unselected
        handleChange(
          updateCheckboxGroupValue(defaultCheckboxGroupValue, selectValue)
        );
      }

      // if formio is handling values as CheckBoxOptions
      if (isArrayOfCheckboxOptions(value)) {
        const checkboxOptions = value.filter(checkboxOption =>
          filterOther(checkboxOption, isFilterOther, otherItem.selectLabel)
        );

        // new Checkbox selected
        if (newValue) {
          handleChange([...checkboxOptions, option]);
          return;
        }

        // Checkbox unselected
        handleChange(updateCheckboxGroupValue(checkboxOptions, selectValue));
      }
    },
    [defaultValue, handleChange, otherItem.selectLabel, translatedItems, value]
  );

  return (
    <CheckboxGroup
      label={label}
      error={errorMessage}
      readOnly={readOnly}
      required={required}
      className={settings.classNames}
      layout="COLUMN"
    >
      {translatedItems.map(option => (
        <CheckboxGroupItem
          key={`${option.selectLabel}-${option.selectValue}`}
          label={option.selectLabel}
          value={option.selected}
          readOnly={readOnly}
          disabled={readOnly && !option.selected}
          onChange={newValue =>
            onChangeWrapper({
              selectValue: option.selectValue,
              newValue
            })
          }
        />
      ))}
      {otherItem && (
        // need to render text input and checkbox in the same 'li' element because of WCAG
        <li className="ehs-checkbox-group-item ehs-other-checkbox-option">
          <HBox className="ehs-other-checkbox-option">
            <Checkbox
              key={`${otherItem.selectLabel}`}
              label={otherItem.translatedLabel}
              aria-label={otherItem.translatedLabel}
              value={otherItem.isSelected}
              readOnly={readOnly}
              disabled={readOnly}
              defaultValue={false}
              onChange={() => {
                otherItem.setSelected(prev => {
                  onChangeWrapper({
                    selectValue: otherItem.selectLabel,
                    newValue: !prev
                  });
                  return !prev;
                });
                otherItem.setValue('');
              }}
            />
            <div
              className="ehs-other-input-wrapper"
              aria-hidden={!otherItem.isSelected}
            >
              <TextInput
                ref={otherTextInputRef}
                label={otherItem.translatedLabel}
                aria-label={otherItem.translatedLabel}
                readOnly={readOnly}
                disabled={readOnly && !otherItem.isSelected}
                type="text"
                value={otherItem.selectValue}
                required={readOnly ? false : otherItem.isSelected}
                onChange={newText => {
                  otherItem.setValue(newText);
                  // pass selectLabel as selectValue, since selectValue is not static
                  // and otherwise we cannot tell formio that the other option has changed
                  onChangeWrapper({
                    selectValue: otherItem.selectLabel,
                    newValue: otherItem.isSelected
                  });
                }}
                onFocus={() => {
                  otherItem.setSelected(true);
                }}
              />
            </div>
          </HBox>
        </li>
      )}
    </CheckboxGroup>
  );
}
